import React from 'react'
import {formatPrice} from '../../utils/currency-format'
import {getAmountToQuantityMap} from '../../utils/lift-lead-options'
import {getProductIcon} from '../../core/shared/ProductIcons'

import {
  ItemRow,
  ItemInfo,
  ItemName,
  ItemIcon,
  ItemPriceWrapper,
  ItemPrice,
  ItemSub,
} from '../../features/OrderSummary/OrderSummary.styles'

const computeCartItemPrice = (
  item,
  {isToday = false, paymentPreview, is5Order = false, country = 'US'} = {},
) => {
  // Coupon-name rows are a caption-only line (see CartItem below) synthesized
  // without a `currency`, since they carry no price of their own — the
  // actual discount is the separate `isIncentive` line. Without this guard,
  // `currency?.unitAmount * itemQuantity` below is NaN and poisons whatever
  // location group's total this item's summed into.
  if (item?.isCouponName) return 0
  // Already on the account (upgrade flow): shown as "Included" today, nothing charged.
  if (isToday && item?.isExisting) return 0
  const nameLower = item?.name?.toLowerCase() ?? ''
  const isQuantityManipulator =
    (nameLower.includes('seo') &&
      item?.name !== 'SEO Keywords' &&
      item?.name !== 'AEO & SEO PowerBoost') ||
    nameLower.includes('thryv leads') ||
    item?.code?.includes('addon-4-socialads')
  const isLeadsLift =
    item?.code === 'addon-5-leadslift-m2m' ||
    item?.code === 'addon-4-leadslift-m2m'

  const isSocialAds =
    isToday &&
    paymentPreview &&
    (item?.code === 'addon-socialadsbronze-5-m2m' ||
      item?.code === 'addon-socialadssilver-5-m2m' ||
      item?.code === 'addon-socialadsgold-5-m2m' ||
      item?.code === 'addon-5-ai-webbuilder')
      ? true
      : false
  const itemQuantity =
    (isToday && paymentPreview && isQuantityManipulator) ||
    (isToday && paymentPreview && !is5Order)
      ? 1
      : item.quantity || 1

  const isAlphaSeoUpgrade =
    isToday &&
    paymentPreview &&
    (item.name === 'AEO & SEO' ||
      item.name === 'SEO Keywords' ||
      item.name === 'AEO & SEO PowerBoost')
      ? true
      : false
  const currency = isToday
    ? item.currency
    : item.nextIntervalCurrency || item.currency
  let planPrice = currency?.unitAmount * itemQuantity

  // When a per-location preview is available (multi-location orders), derive
  // the item price from the preview's centers/apps subtotal rather than the
  // catalog unitAmount, which may differ (proration, coupon adjustment, etc.).
  if (isToday && paymentPreview && item.code) {
    const previewEntry = [
      ...(paymentPreview.centers ?? []),
      ...(paymentPreview.apps ?? []),
    ].find(e => e.planCode === item.code || e.addonCode === item.code)
    if (previewEntry != null) {
      const isAusNz =
        ['AUD', 'NZD'].includes(paymentPreview.currency) ||
        ['AUD', 'NZD'].includes(currency?.currency)
      planPrice = isAusNz
        ? previewEntry.subtotal + previewEntry.tax
        : previewEntry.subtotal
    }
  }
  if (item.pricingModel === 'ramp') {
    const rampInterval = item.rampIntervals?.[1]
    const matchedCurrency = rampInterval?.currencies?.find(
      c => c.currency === currency?.currency,
    )
    planPrice = (matchedCurrency?.unitAmount ?? 0) * itemQuantity
  }
  if (isAlphaSeoUpgrade) {
    planPrice = currency?.unitAmount
  }
  if (isLeadsLift) {
    if (isToday && paymentPreview) {
      planPrice = currency?.unitAmount
    } else {
      const entries = Object.entries(getAmountToQuantityMap(country))
      const closest = entries.reduce((prev, curr) =>
        Math.abs(curr[1] - itemQuantity) < Math.abs(prev[1] - itemQuantity)
          ? curr
          : prev,
      )
      planPrice = Number(closest[0])
    }
  }
  if (
    currency?.currency === 'AUD' &&
    (item.code === 'seo-5-thryv-m2m' ||
      item.code === 'seombg-5-thryv-m2m' ||
      item.code === 'addon-4-seombg-m2m' ||
      item.code === 'addon-4-seo-m2m')
  ) {
    planPrice = planPrice + planPrice * 0.1
  }
  if (
    currency?.currency === 'AUD' &&
    !paymentPreview &&
    (item.code === 'seobase-5-thryv-m2m' ||
      item.code === 'addon-4-seobase-m2m' ||
      item.code === 'addon-4-seopb-m2m' ||
      item.code === 'addon-4-seokey-m2m' ||
      item.code === 'addon-5-seopb-m2m' ||
      item.code === 'addon-5-seokey-m2m')
  ) {
    planPrice = planPrice * 1.1
  }
  if (isSocialAds) {
    const itemCode = item.code
    const socialAdsAddOn = paymentPreview?.apps?.find(({addonCode}) => {
      return addonCode === itemCode
    })
    if (currency?.currency === 'AUD')
      planPrice = socialAdsAddOn?.subtotal + socialAdsAddOn?.tax
    else planPrice = socialAdsAddOn?.subtotal
  }
  if (
    (item.code === 'addon-5-keap-addons' ||
      item.code === 'addon-5-keap-hourlydatasvc' ||
      item.code === 'addon-5-keap-custdfy-ot') &&
    paymentPreview &&
    isToday
  ) {
    planPrice = currency?.unitAmount
  }

  return planPrice
}

const CartItem = ({item, isToday = false, paymentPreview, country = 'US'}) => {
  if (item?.isCouponName)
    return (
      <p className="paragraph-cart-item" style={{paddingTop: '12px'}}>
        {item?.description || item?.name}
      </p>
    )

  const nameLower = item?.name?.toLowerCase() ?? ''
  const isSEOOrTL =
    (nameLower.includes('seo') &&
      item.name !== 'SEO Keywords' &&
      item.name !== 'AEO & SEO PowerBoost') ||
    nameLower.includes('thryv leads')
  const price = computeCartItemPrice(item, {isToday, paymentPreview, country})
  const isIncluded = isToday && Boolean(item.isExisting)
  const isSelected = price > 0
  return (
    <>
      <ItemRow selected={isSelected || item.isIncentive}>
        {!item.isIncentive &&
        !item.isCoupon &&
        item.prodType === 'plan' &&
        !item.groupTitle ? (
          <ItemIcon src={item.img || getProductIcon(item.name)} alt="" />
        ) : null}
        <ItemInfo>
          <ItemName selected={isSelected}>
            {item.name === 'Thryv Business Center'
              ? 'Business Center'
              : item.name}
            {item.quantity && item.quantity > 1 && !isSEOOrTL
              ? ` (${item.quantity}x)`
              : ''}
          </ItemName>
          {item?.addons
            ?.filter(a => a.isEntitlement)
            .map(entitlement => (
              <ItemSub key={entitlement.code}>{entitlement.name}</ItemSub>
            ))}
          {item?.addons
            ?.filter(a => a.prodType === 'itemBasedAddon' && !a.isEntitlement)
            .map(addon => (
              <ItemSub key={addon.code}>
                {addon.name}
                {addon.quantity && addon.quantity > 1
                  ? ` (${addon.quantity}x)`
                  : ''}
              </ItemSub>
            ))}
        </ItemInfo>
        <ItemPriceWrapper>
          {!item.isIncentive && (
            <ItemPrice>
              {isIncluded ? 'Included' : formatPrice(price, 1, '', country)}
            </ItemPrice>
          )}
          {item?.addons
            ?.filter(a => a.prodType === 'itemBasedAddon')
            .map(addon => (
              <p
                key={addon.code}
                style={{
                  fontFamily: 'Open Sans',
                  fontWeight: 400,
                  fontSize: '11px',
                  margin: '0px',
                  color: '#22c55e',
                }}
              >
                {isIncluded || (isToday && addon.isExisting)
                  ? 'Included'
                  : formatPrice(
                      (() => {
                        const previewAddon = paymentPreview?.apps?.find(
                          ({addonCode}) => addonCode === addon.code,
                        )
                        if (previewAddon) {
                          const isAusNz = ['AUD', 'NZD'].includes(
                            paymentPreview?.currency,
                          )
                          return isAusNz
                            ? previewAddon.total
                            : previewAddon.subtotal
                        }
                        const currencyCode = item.currency?.currency
                        const addonCurrency = addon.currencies?.find(
                          c => c.currency === currencyCode,
                        )
                        const addonPrice =
                          (addonCurrency?.unitAmount || 0) *
                          (addon.quantity || 1)
                        const isSeobaseAddon = [
                          'addon-5-seopb-m2m',
                          'addon-5-seokey-m2m',
                          'addon-4-seopb-m2m',
                          'addon-4-seokey-m2m',
                        ].includes(addon.code)
                        return currencyCode === 'AUD' && isSeobaseAddon
                          ? addonPrice * 1.1
                          : addonPrice
                      })(),
                      1,
                      '',
                      country,
                    )}
              </p>
            ))}
        </ItemPriceWrapper>
      </ItemRow>
    </>
  )
}

export {CartItem, computeCartItemPrice}
